import { useCallback, useState } from 'react';
import { useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import {
  docToPlainText,
  extractEmoji,
  newId,
  plainTextToDoc,
  type Message,
  type MessageTask,
  type ReactionSummary,
  type RichTextDoc,
} from '@avslack/shared';
import {
  deleteMessage as deleteMessageRequest,
  editMessage as editMessageRequest,
  getMessage,
  getMessages,
  sendMessage,
  setMessageImportant,
  togglePin as togglePinRequest,
  toggleReaction as toggleReactionRequest,
  toggleSaved as toggleSavedRequest,
  ApiRequestError,
} from '../lib/api';
import { refreshFrequentEmoji } from './emoji-usage';
import { addToOutbox, isRetryableFailure, QUEUED_REASON, removeFromOutbox } from './outbox';

/** A kliens oldalon egy uzenet lehet meg uton levo vagy sikertelen. */
export interface LocalMessage extends Message {
  pending?: boolean;
  failed?: boolean;
  /**
   * MIERT nem ment el.
   *
   * Enelkul minden kudarc egyforman nezett ki ("Nem sikerult elkuldeni"), es
   * az Ujra gomb ugyanabba a hibaba futott ujra meg ujra. A felhasznalo azt
   * latta, hogy az alkalmazas veletlenszeruen elnyel uzeneteket - pedig a
   * szerver pontosan megmondta, mi a baj, csak senki nem tovabbitotta.
   */
  failedReason?: string;
  /**
   * A mellekletek azonositoi - az UJRAKULDESHEZ.
   *
   * A `files` a kesz mellekleteket irja le, de ujrakuldeskor az azonositok
   * kellenek. Enelkul a sikertelen uzenet mellekletei csendben lemaradtak
   * volna a masodik probalkozasnal.
   */
  pendingFileIds?: string[];
}

/** A szerver uzenete, ha van; kulonben egy altalanos mondat. */
function kudarcOka(error: unknown): string {
  if (error instanceof ApiRequestError) return error.message;
  return 'Nem sikerült elküldeni.';
}

/**
 * Az uzenet szovege VAGOLAPRA masolhato alakban.
 *
 * A dokumentum a mervado, ha van: abbol ugyanaz a sima szoveg keszul, amit a
 * kereses es az ertesites is hasznal - a listak, idezetek es emlitesek igy
 * olvashato alakban kerulnek at, nem jelolokent. A regi, dokumentum nelkuli
 * uzeneteknel (bot, webhook) marad a nyers szoveg.
 *
 * Ures sztringet is adhat: egy csak-csatolmany uzenetnek nincs mit masolni.
 * A hivo dolga, hogy ilyenkor ne kinaljon gombot.
 */
export function messagePlainText(message: Message): string {
  return (message.body ? docToPlainText(message.body) : message.text).trim();
}

export interface ChannelMessages {
  messages: LocalMessage[];
  /** Van-e meg REGEBBI uzenet a betoltott ablak elott. */
  hasMore: boolean;
  /**
   * Van-e meg UJABB uzenet a betoltott ablak utan.
   *
   * Permalinkkel egy regi uzenetre ugorva az ablak a csatorna kozepen all.
   * Amig ez igaz, a lista alja NEM a beszelgetes alja: nem szabad oda
   * tapadni, es az uj uzeneteket sem szabad a vegere fuzni - azok tobb szaz
   * sorral kesobb jonnek.
   */
  hasMoreNewer: boolean;
  channelLastSeq: number;
  /**
   * TERERO NELKUL nyitott csatorna helyorzoje (K2, 4. csomag): a szerver nem
   * volt elerheto. A lista ures, de a kimeno sor uzenetei latszanak benne, es
   * a kapcsolat visszaterteval ujratoltodik (data/outbox.ts).
   */
  offline?: boolean;
}

export const messagesKey = (channelId: string) => ['messages', channelId] as const;

const PAGE_SIZE = 50;

/**
 * Osszefuzes: azonosito szerint deduplikal, sorszam szerint rendez.
 *
 * A meg uton levo (pending) uzenetet a `clientMsgId` alapjan valtja le a szerver
 * vegleges valtozata - ettol nem villan meg ket peldany az optimista megjelenites
 * es a WebSocket-esemeny kozott.
 */
export function mergeMessages(existing: LocalMessage[], incoming: LocalMessage[]): LocalMessage[] {
  const byId = new Map<string, LocalMessage>();
  const confirmedClientIds = new Set<string>();

  for (const message of [...existing, ...incoming]) {
    if (!message.pending && message.clientMsgId) confirmedClientIds.add(message.clientMsgId);
    byId.set(message.id, { ...byId.get(message.id), ...message });
  }

  return [...byId.values()]
    .filter((m) => !(m.pending && m.clientMsgId && confirmedClientIds.has(m.clientMsgId)))
    .sort((a, b) => {
      // A meg uton levo uzenetnek nincs vegleges sorszama - mindig a vegere kerul.
      if (a.pending !== b.pending) return a.pending ? 1 : -1;
      if (a.pending && b.pending) return a.createdAt.localeCompare(b.createdAt);
      return a.seq - b.seq;
    });
}

export function setChannelMessages(
  queryClient: QueryClient,
  channelId: string,
  update: (current: ChannelMessages) => ChannelMessages,
): void {
  const current = queryClient.getQueryData<ChannelMessages>(messagesKey(channelId));
  if (!current) return;
  queryClient.setQueryData(messagesKey(channelId), update(current));
}

/** A legutolso VEGLEGES (nem pending) sorszam - a hezag-detektalas alapja. */
function lastConfirmedSeq(data: ChannelMessages): number {
  for (let i = data.messages.length - 1; i >= 0; i -= 1) {
    const message = data.messages[i];
    if (message && !message.pending) return message.seq;
  }
  return 0;
}

/**
 * HEZAG-POTLAS.
 *
 * Ha a kliens olyan sorszamot lat, ami nem a kovetkezo a sorban, akkor kozben
 * kimaradt valami (elveszett keret, alvo ful, halozatvaltas). Ilyenkor REST-tel
 * potoljuk a hianyzo reszt. Enelkul a hianyzo uzenet NEMA maradna - ez a
 * chat-alkalmazasok legalattomosabb hibaja.
 */
/**
 * Ennel nagyobb lemaradasnal mar nem potolunk, hanem ujratoltjuk a legfrissebb
 * oldalt. Permalinkkel visszaugorva egy fel eves uzenetre kulonben tobb ezer
 * uzenetet kernenk le feleslegesen.
 */
const MAX_GAP_FILL = 200;

export async function fillGap(queryClient: QueryClient, channelId: string): Promise<void> {
  const current = queryClient.getQueryData<ChannelMessages>(messagesKey(channelId));
  if (!current) return;

  /*
    Permalink-ablakban NEM potolunk.

    Ilyenkor a "hezag" nem hiba: a felhasznalo szandekosan all a beszelgetes
    kozepen, es tudjuk is rola, hogy lefele van meg mit tolteni. A potlas
    vagy oda nem illo uzeneteket fuzne az ablak vegere, vagy - nagy tavolsagnal
    - kirantana a felhasznalot a legfrissebb oldalra olvasas kozben.
  */
  if (current.hasMoreNewer) {
    const latestSeq = (await getMessages(channelId, { limit: 1 })).channelLastSeq;
    setChannelMessages(queryClient, channelId, (data) => ({
      ...data,
      channelLastSeq: Math.max(data.channelLastSeq, latestSeq),
    }));
    return;
  }

  const from = lastConfirmedSeq(current);

  if (current.channelLastSeq - from > MAX_GAP_FILL) {
    const latest = await getMessages(channelId, { limit: PAGE_SIZE });
    queryClient.setQueryData<ChannelMessages>(messagesKey(channelId), {
      messages: latest.messages,
      hasMore: latest.hasMore,
      hasMoreNewer: false,
      channelLastSeq: latest.channelLastSeq,
    });
    return;
  }

  const response = await getMessages(channelId, { afterSeq: from, limit: MAX_GAP_FILL });
  if (response.messages.length === 0 && response.channelLastSeq === current.channelLastSeq) return;

  setChannelMessages(queryClient, channelId, (data) => ({
    ...data,
    messages: mergeMessages(data.messages, response.messages),
    channelLastSeq: Math.max(data.channelLastSeq, response.channelLastSeq),
  }));
}

/** Ujracsatlakozas utan minden betoltott csatornat ujra szinkronizalunk. */
export async function resyncAllChannels(queryClient: QueryClient): Promise<void> {
  const loaded = queryClient
    .getQueryCache()
    .findAll({ queryKey: ['messages'] })
    .map((query) => query.queryKey[1])
    .filter((id): id is string => typeof id === 'string');

  await Promise.all(loaded.map((channelId) => fillGap(queryClient, channelId)));
}

/** Bejovo WebSocket-uzenet feldolgozasa, hezag-ellenorzessel. */
export async function applyIncomingMessage(
  queryClient: QueryClient,
  message: Message,
): Promise<void> {
  const current = queryClient.getQueryData<ChannelMessages>(messagesKey(message.channelId));
  if (!current) return; // nincs betoltve - majd a megnyitasakor toltodik

  /*
    Permalink-ablakban az uj uzenet nem ide tartozik: a betoltott ablak utan
    meg tobb szaz masik uzenet all. Csak a csatorna sorszamat jegyezzuk fel -
    igy a "vissza a legfrissebbre" gomb tudja, hogy van hova visszaterni.
  */
  if (current.hasMoreNewer) {
    setChannelMessages(queryClient, message.channelId, (data) => ({
      ...data,
      channelLastSeq: Math.max(data.channelLastSeq, message.seq),
    }));
    return;
  }

  const expected = lastConfirmedSeq(current) + 1;
  if (message.seq > expected) {
    await fillGap(queryClient, message.channelId);
    return; // a potlas mar tartalmazza ezt az uzenetet is
  }

  setChannelMessages(queryClient, message.channelId, (data) => ({
    ...data,
    messages: mergeMessages(data.messages, [message]),
    channelLastSeq: Math.max(data.channelLastSeq, message.seq),
  }));
}

// ─── Hookok ───────────────────────────────────────────────────────────────────

export function useMessages(channelId: string | undefined) {
  return useQuery({
    queryKey: messagesKey(channelId ?? ''),
    enabled: Boolean(channelId),
    // A cache-t a WebSocket tartja naprakeszen, nem ismetelt lekeressel.
    staleTime: Infinity,
    queryFn: async (): Promise<ChannelMessages> => {
      let response: Awaited<ReturnType<typeof getMessages>>;
      try {
        response = await getMessages(channelId!, { limit: PAGE_SIZE });
      } catch (error) {
        if (!isRetryableFailure(error)) throw error;
        return {
          messages: [],
          hasMore: false,
          hasMoreNewer: false,
          channelLastSeq: 0,
          offline: true,
        };
      }
      return {
        messages: response.messages,
        hasMore: response.hasMore,
        hasMoreNewer: false,
        channelLastSeq: response.channelLastSeq,
      };
    },
  });
}

export function useLoadOlderMessages(channelId: string | undefined) {
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState(false);

  const loadOlder = useCallback(async () => {
    if (!channelId || loading) return;
    const current = queryClient.getQueryData<ChannelMessages>(messagesKey(channelId));
    if (!current || !current.hasMore) return;

    const oldest = current.messages.find((m) => !m.pending);
    if (!oldest) return;

    setLoading(true);
    try {
      const response = await getMessages(channelId, { beforeSeq: oldest.seq, limit: PAGE_SIZE });
      setChannelMessages(queryClient, channelId, (data) => ({
        ...data,
        messages: mergeMessages(data.messages, response.messages),
        hasMore: response.hasMore,
      }));
    } finally {
      setLoading(false);
    }
  }, [channelId, loading, queryClient]);

  return { loadOlder, loading };
}

/**
 * Elorefele lapozas - CSAK permalink-ablakban van ertelme.
 *
 * A par sora ez a {@link useLoadOlderMessages}-nek: ha a betoltott ablak nem a
 * beszelgetes vegen all, lefele gorgetve a kovetkezo oldal ide toltodik be.
 */
export function useLoadNewerMessages(channelId: string | undefined) {
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState(false);

  const loadNewer = useCallback(async () => {
    if (!channelId || loading) return;
    const current = queryClient.getQueryData<ChannelMessages>(messagesKey(channelId));
    if (!current || !current.hasMoreNewer) return;

    const newest = lastConfirmedSeq(current);
    if (newest === 0) return;

    setLoading(true);
    try {
      const response = await getMessages(channelId, { afterSeq: newest, limit: PAGE_SIZE });
      setChannelMessages(queryClient, channelId, (data) => ({
        ...data,
        messages: mergeMessages(data.messages, response.messages),
        hasMoreNewer: response.hasMoreNewer,
        channelLastSeq: Math.max(data.channelLastSeq, response.channelLastSeq),
      }));
    } finally {
      setLoading(false);
    }
  }, [channelId, loading, queryClient]);

  return { loadNewer, loading };
}

export function useSendMessage(channelId: string | undefined) {
  const queryClient = useQueryClient();

  return useCallback(
    async (
      value: { text: string; body?: RichTextDoc | undefined },
      authorId: string,
      parentId: string | null = null,
      fileIds: string[] = [],
      isImportant = false,
      isTask = false,
    ) => {
      if (!channelId) return;
      const trimmed = value.text.trim();
      // Fajl mellett ures szoveg is elmehet.
      if (!trimmed && fileIds.length === 0) return;

      /*
        Kuldes elott vissza a beszelgetes vegere.

        Permalinkkel visszaugorva a felhasznalo a csatorna kozepen all. Az uj
        uzenet a folyam VEGERE megy - ha itt nem ugranank vissza, a sajat
        uzenetunk egy tavali beszelgetes ala kerulne a kepernyon, mintha oda
        valaszoltunk volna.
      */
      const current = queryClient.getQueryData<ChannelMessages>(messagesKey(channelId));
      if (current?.hasMoreNewer) await jumpToLatest(queryClient, channelId);

      const clientMsgId = newId();
      const optimistic: LocalMessage = {
        id: `pending-${clientMsgId}`,
        channelId,
        seq: Number.MAX_SAFE_INTEGER,
        userId: authorId,
        parentId,
        text: trimmed,
        // Az optimista megjelenites is a dokumentumot rajzolja, hogy a
        // formazas ne "ugorjon be" utolag a szerver valaszakor.
        body: trimmed ? (value.body ?? plainTextToDoc(trimmed)) : null,
        subtype: 'standard',
        meta: null,
        clientMsgId,
        pendingFileIds: fileIds,
        createdAt: new Date().toISOString(),
        editedAt: null,
        deletedAt: null,
        reactions: [],
        files: [],
        links: [],
        isPinned: false,
        // A kiemelt hatter AZONNAL latszik, nem a szerver valaszakor - kulonben
        // egy pillanatra sima uzenetkent villanna fel, amit epp megjeloltunk.
        isImportant,
        task: isTask ? { done: false, doneBy: null, doneAt: null } : null,
        isSaved: false,
        threadReplyCount: 0,
        threadLastReplyAt: null,
        pending: true,
      };

      setChannelMessages(queryClient, channelId, (data) => ({
        ...data,
        messages: mergeMessages(data.messages, [optimistic]),
      }));

      try {
        const result = await sendMessage(channelId, {
          text: trimmed,
          clientMsgId,
          parentId,
          ...(value.body && trimmed ? { body: value.body } : {}),
          ...(fileIds.length > 0 ? { fileIds } : {}),
          ...(isImportant ? { isImportant } : {}),
          ...(isTask ? { isTask } : {}),
        });
        setChannelMessages(queryClient, channelId, (data) => ({
          ...data,
          messages: mergeMessages(
            data.messages.filter((m) => m.id !== optimistic.id),
            [result.message],
          ),
          channelLastSeq: Math.max(data.channelLastSeq, result.message.seq),
        }));
        if (containsEmoji(trimmed)) refreshFrequentEmoji(queryClient);
        if (isTask) void queryClient.invalidateQueries({ queryKey: ['tasks', channelId] });
      } catch (error) {
        // Nem tuntetjuk el az uzenetet - a felhasznalo lassa, hogy nem ment el,
        // es tudja ujraprobalni. A `clientMsgId` miatt az ujrakuldes biztonsagos.
        // Halozati hibanal a kimeno sorba kerul, es magatol elmegy (outbox.ts).
        const queued = isRetryableFailure(error);
        if (queued) {
          addToOutbox({
            channelId,
            clientMsgId,
            authorId,
            parentId,
            text: trimmed,
            ...(value.body && trimmed ? { body: value.body } : {}),
            fileIds,
            isImportant,
            ...(isTask ? { isTask } : {}),
            createdAt: optimistic.createdAt,
          });
        }
        setChannelMessages(queryClient, channelId, (data) => ({
          ...data,
          messages: data.messages.map((m) =>
            m.id === optimistic.id
              ? { ...m, failed: true, failedReason: queued ? QUEUED_REASON : kudarcOka(error) }
              : m,
          ),
        }));
      }
    },
    [channelId, queryClient],
  );
}

/** Van-e az uzenetben emoji - csak akkor erdemes a "Gyakori" sort ujrakerni. */
function containsEmoji(text: string): boolean {
  const found = extractEmoji(text);
  return found.builtin.length > 0 || found.custom.length > 0;
}

/** Sikertelen uzenet ujrakuldese - ugyanazzal a clientMsgId-vel, ezert biztonsagos. */
export function useRetryMessage(channelId: string | undefined) {
  const queryClient = useQueryClient();

  return useCallback(
    async (message: LocalMessage) => {
      if (!channelId || !message.clientMsgId) return;

      setChannelMessages(queryClient, channelId, (data) => ({
        ...data,
        messages: data.messages.map((m) =>
          m.id === message.id ? { ...m, failed: false, pending: true } : m,
        ),
      }));

      try {
        const result = await sendMessage(channelId, {
          text: message.text,
          clientMsgId: message.clientMsgId,
          parentId: message.parentId,
          ...(message.body ? { body: message.body } : {}),
          // A mellekletek NEM eshetnek ki az ujrakuldesnel: az csendes
          // adatvesztes lenne - az uzenet elmenne, a csatolmany nelkul.
          ...(message.pendingFileIds?.length ? { fileIds: message.pendingFileIds } : {}),
          // Es a "fontos" jeloles sem: az is a kuldo szandeka volt.
          ...(message.isImportant ? { isImportant: true } : {}),
          ...(message.task ? { isTask: true } : {}),
        });
        removeFromOutbox(message.clientMsgId);
        setChannelMessages(queryClient, channelId, (data) => ({
          ...data,
          messages: mergeMessages(
            data.messages.filter((m) => m.id !== message.id),
            [result.message],
          ),
        }));
      } catch (error) {
        const queued = isRetryableFailure(error);
        if (!queued) removeFromOutbox(message.clientMsgId);
        setChannelMessages(queryClient, channelId, (data) => ({
          ...data,
          messages: data.messages.map((m) =>
            m.id === message.id
              ? { ...m, failed: true, failedReason: queued ? QUEUED_REASON : kudarcOka(error) }
              : m,
          ),
        }));
      }
    },
    [channelId, queryClient],
  );
}

// ─── Meglevo uzenetek modositasa ──────────────────────────────────────────────

/**
 * Szerkesztes, torles es reakcio NEM kap uj sorszamot: ezek azonosito alapjan
 * modositanak egy meglevo uzenetet. Ha egy ilyen esemeny elveszik, a kovetkezo
 * elozmeny-lekeres helyreallitja - ezert nem kell hozzajuk hezag-detektalas.
 */
function patchMessage(
  queryClient: QueryClient,
  channelId: string,
  messageId: string,
  patch: (message: LocalMessage) => LocalMessage,
): void {
  setChannelMessages(queryClient, channelId, (data) => ({
    ...data,
    messages: data.messages.map((m) => (m.id === messageId ? patch(m) : m)),
  }));
}

export function applyMessageUpdate(queryClient: QueryClient, message: Message): void {
  patchMessage(queryClient, message.channelId, message.id, (current) => ({
    ...current,
    ...message,
  }));
}

export function applyMessageDeleted(
  queryClient: QueryClient,
  channelId: string,
  messageId: string,
): void {
  patchMessage(queryClient, channelId, messageId, (current) => ({
    ...current,
    text: '',
    reactions: [],
    deletedAt: new Date().toISOString(),
  }));
}

export function applyReactionChange(
  queryClient: QueryClient,
  channelId: string,
  messageId: string,
  reactions: ReactionSummary[],
): void {
  patchMessage(queryClient, channelId, messageId, (current) => ({ ...current, reactions }));
}

export function applyThreadUpdate(
  queryClient: QueryClient,
  channelId: string,
  parentId: string,
  replyCount: number,
  lastReplyAt: string | null,
): void {
  patchMessage(queryClient, channelId, parentId, (current) => ({
    ...current,
    threadReplyCount: replyCount,
    threadLastReplyAt: lastReplyAt,
  }));
}

// ─── Muveletek ────────────────────────────────────────────────────────────────

export function useEditMessage(channelId: string | undefined) {
  const queryClient = useQueryClient();

  return useCallback(
    async (messageId: string, value: { text: string; body?: RichTextDoc | undefined }) => {
      if (!channelId) return;
      const message = await editMessageRequest(messageId, value.text, value.body);
      applyMessageUpdate(queryClient, message);
      /*
        Az elozmeny is valtozott: eggyel tobb korabbi valtozat van. Enelkul a
        "(szerkesztve)" mogott a MEGELOZO lekerdezes eredmenye maradna - vagyis
        az imenti valtozat epp abbol hianyozna, aminek a megorzese az egesz
        funkcio lenyege.
      */
      void queryClient.invalidateQueries({ queryKey: ['message-revisions', messageId] });
    },
    [channelId, queryClient],
  );
}

export function useDeleteMessage(channelId: string | undefined) {
  const queryClient = useQueryClient();

  return useCallback(
    async (messageId: string) => {
      if (!channelId) return;
      await deleteMessageRequest(messageId);
      applyMessageDeleted(queryClient, channelId, messageId);
    },
    [channelId, queryClient],
  );
}

/**
 * Reakcio valtasa optimistan: a szam azonnal ugrik, mert egy fel masodperces
 * keses egy ilyen apro visszajelzesnel nagyon zavaro. A szerver valasza utan
 * a hiteles allapotra allunk.
 */
export function useToggleReaction(channelId: string | undefined, meId: string) {
  const queryClient = useQueryClient();

  return useCallback(
    async (messageId: string, emoji: string) => {
      if (!channelId) return;

      patchMessage(queryClient, channelId, messageId, (current) => {
        const existing = current.reactions.find((r) => r.emoji === emoji);
        const mine = existing?.userIds.includes(meId) ?? false;

        const next = mine
          ? current.reactions
              .map((r) =>
                r.emoji === emoji
                  ? { ...r, count: r.count - 1, userIds: r.userIds.filter((id) => id !== meId) }
                  : r,
              )
              .filter((r) => r.count > 0)
          : existing
            ? current.reactions.map((r) =>
                r.emoji === emoji ? { ...r, count: r.count + 1, userIds: [...r.userIds, meId] } : r,
              )
            : [...current.reactions, { emoji, count: 1, userIds: [meId] }];

        return { ...current, reactions: next };
      });

      try {
        const result = await toggleReactionRequest(messageId, emoji);
        applyReactionChange(queryClient, channelId, messageId, result.reactions);
        if (result.added) refreshFrequentEmoji(queryClient);
      } catch {
        // Sikertelen valtas: a szerver allapotara allunk vissza.
        await fillGap(queryClient, channelId);
      }
    },
    [channelId, meId, queryClient],
  );
}

// ─── Kituzes, mentes, permalink ───────────────────────────────────────────────

export function applyPinChange(
  queryClient: QueryClient,
  channelId: string,
  messageId: string,
  isPinned: boolean,
): void {
  patchMessage(queryClient, channelId, messageId, (current) => ({ ...current, isPinned }));
  void queryClient.invalidateQueries({ queryKey: ['pins', channelId] });
}

/**
 * A fontos-jeloles valtozott - barmelyik fulon, barki kezetol.
 *
 * A kereso talalatai is elavulnak tole (van `is:important` szuro), ezert
 * azokat ujra kell kerdezni.
 */
export function applyImportantChange(
  queryClient: QueryClient,
  channelId: string,
  messageId: string,
  isImportant: boolean,
): void {
  patchMessage(queryClient, channelId, messageId, (current) => ({ ...current, isImportant }));
  void queryClient.invalidateQueries({ queryKey: ['channel-search'] });
  void queryClient.invalidateQueries({ queryKey: ['search'] });
}

/** A feladat-jeloles vagy -kipipalas valtozott - az uzenet es a fejlec doboza is frissul. */
export function applyTaskChange(
  queryClient: QueryClient,
  channelId: string,
  messageId: string,
  task: MessageTask | null,
): void {
  patchMessage(queryClient, channelId, messageId, (current) => ({ ...current, task }));
  void queryClient.invalidateQueries({ queryKey: ['tasks', channelId] });
}

export function applySavedChange(
  queryClient: QueryClient,
  channelId: string,
  messageId: string,
  isSaved: boolean,
): void {
  patchMessage(queryClient, channelId, messageId, (current) => ({ ...current, isSaved }));
  void queryClient.invalidateQueries({ queryKey: ['saved'] });
}

export function useTogglePin(channelId: string | undefined) {
  const queryClient = useQueryClient();

  return useCallback(
    async (messageId: string) => {
      if (!channelId) return;
      const result = await togglePinRequest(messageId);
      applyPinChange(queryClient, channelId, messageId, result.isPinned);
    },
    [channelId, queryClient],
  );
}

export function useToggleImportant(channelId: string | undefined) {
  const queryClient = useQueryClient();

  return useCallback(
    async (messageId: string, isImportant: boolean) => {
      if (!channelId) return;
      /*
        Elobb a felulet, aztan a szerver.

        A jeloles az EGESZ kartya szinet megvaltoztatja; ha ezt a halozat
        koruthossza utan tennenk, a kattintas utan masodpercekig nem tortenne
        semmi lathato. Hiba eseten visszaall - a szerver valasza a dontő.
      */
      applyImportantChange(queryClient, channelId, messageId, isImportant);
      try {
        const result = await setMessageImportant(messageId, isImportant);
        applyImportantChange(queryClient, channelId, messageId, result.isImportant);
      } catch (error) {
        applyImportantChange(queryClient, channelId, messageId, !isImportant);
        throw error;
      }
    },
    [channelId, queryClient],
  );
}

export function useToggleSaved(channelId: string | undefined) {
  const queryClient = useQueryClient();

  return useCallback(
    async (messageId: string) => {
      if (!channelId) return;
      const result = await toggleSavedRequest(messageId);
      applySavedChange(queryClient, channelId, messageId, result.isSaved);
    },
    [channelId, queryClient],
  );
}

/**
 * Ugras egy uzenetre (permalink).
 *
 * Ha az uzenet nincs a betoltott ablakban, a szervertol a KORNYEZETET kerjuk le,
 * es azzal csereljuk le a cache-t - igy egy kattintassal a helyere kerul a
 * beszelgetes, nem kell tobb korben visszafele lapozni.
 */
export async function ensureMessageLoaded(
  queryClient: QueryClient,
  channelId: string,
  messageId: string,
): Promise<boolean> {
  const current = queryClient.getQueryData<ChannelMessages>(messagesKey(channelId));
  if (current?.messages.some((m) => m.id === messageId)) return true;

  const message = await getMessage(messageId).catch(() => null);
  if (!message) return false;

  const window = await getMessages(channelId, { aroundSeq: message.seq, limit: PAGE_SIZE });
  queryClient.setQueryData<ChannelMessages>(messagesKey(channelId), {
    messages: window.messages,
    hasMore: window.hasMore,
    /*
      A LEGFONTOSABB sor ebben a fuggvenyben.

      Enelkul az ablak zsakutca volt: a kliens ugy tudta, hogy a lista alja a
      csatorna alja is egyben. Lefele nem toltott tovabb, a "vissza a
      legfrissebbre" gomb sem jelent meg, es mivel az ablak a gyorsitotarban
      maradt, meg csatornavaltas utan sem allt helyre - csak ujratoltessel.
    */
    hasMoreNewer: window.hasMoreNewer,
    channelLastSeq: window.channelLastSeq,
  });
  return true;
}

/**
 * Vissza a beszelgetes vegere.
 *
 * A permalink-ablakbol EGY lepesben visszahozza a legfrissebb oldalt, ahelyett
 * hogy a felhasznalonak fel evnyi uzeneten kellene vegiggorgetnie.
 */
export async function jumpToLatest(queryClient: QueryClient, channelId: string): Promise<void> {
  const latest = await getMessages(channelId, { limit: PAGE_SIZE });
  queryClient.setQueryData<ChannelMessages>(messagesKey(channelId), {
    messages: latest.messages,
    hasMore: latest.hasMore,
    hasMoreNewer: false,
    channelLastSeq: latest.channelLastSeq,
  });
}
